import * as joint from '@joint/core'

/**
 * C4 Boundary shape - dashed rectangle for grouping elements
 * Used for Software System boundaries in Container/Component views
 */
export const C4Boundary = joint.dia.Element.define(
  'c4.Boundary',
  {
    size: { width: 400, height: 300 },
    attrs: {
      body: {
        refWidth: '100%',
        refHeight: '100%',
        rx: 10,
        ry: 10,
        fill: 'transparent',
        stroke: '#888888',
        strokeWidth: 2,
        strokeDasharray: '8,4',
      },
      titleBackground: {
        refX: 10,
        refY: -14,
        width: 200,
        height: 28,
        fill: '#ffffff',
      },
      titleLabel: {
        refX: 15,
        refY: 0,
        textVerticalAnchor: 'middle',
        textAnchor: 'start',
        fontSize: 14,
        fontWeight: 'bold',
        fill: '#333333',
        fontFamily: 'Open Sans, sans-serif',
        text: '',
      },
      typeLabel: {
        refX: 15,
        refY: -28,
        textVerticalAnchor: 'middle',
        textAnchor: 'start',
        fontSize: 11,
        fill: '#666666',
        fontFamily: 'Open Sans, sans-serif',
        text: '[Software System]',
      },
    },
  },
  {
    markup: [
      {
        tagName: 'rect',
        selector: 'body',
      },
      {
        tagName: 'rect',
        selector: 'titleBackground',
      },
      {
        tagName: 'text',
        selector: 'typeLabel',
      },
      {
        tagName: 'text',
        selector: 'titleLabel',
      },
    ],
  }
)

export interface BoundaryStyle {
  stroke?: string
  color?: string
  background?: string
}

export function createBoundary(
  id: string,
  name: string,
  type: 'SoftwareSystem' | 'Container' | 'Enterprise' = 'SoftwareSystem',
  x: number = 0,
  y: number = 0,
  width: number = 400,
  height: number = 300,
  style?: BoundaryStyle
): joint.dia.Element {
  const typeLabel = type === 'Enterprise'
    ? '[Enterprise]'
    : type === 'Container'
      ? '[Container]'
      : '[Software System]'

  const boundary = new C4Boundary({
    id: `boundary-${id}`,
    position: { x, y },
    size: { width, height },
    attrs: {
      body: {
        ...(style?.stroke && { stroke: style.stroke }),
        ...(style?.background && { fill: style.background }),
      },
      titleLabel: {
        text: name,
        ...(style?.color && { fill: style.color }),
      },
      typeLabel: {
        text: typeLabel,
        ...(style?.color && { fill: style.color }),
      },
    },
    // Mark as background element (should be behind other elements)
    z: -1,
  })

  return boundary
}

/**
 * Calculate boundary dimensions to encompass child elements
 */
export function calculateBoundaryDimensions(
  childPositions: Array<{ x: number; y: number; width: number; height: number }>,
  padding: number = 40
): { x: number; y: number; width: number; height: number } {
  if (childPositions.length === 0) {
    return { x: 0, y: 0, width: 400, height: 300 }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const pos of childPositions) {
    minX = Math.min(minX, pos.x)
    minY = Math.min(minY, pos.y)
    maxX = Math.max(maxX, pos.x + pos.width)
    maxY = Math.max(maxY, pos.y + pos.height)
  }

  return {
    x: minX - padding,
    y: minY - padding - 30, // Extra space for title
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2 + 30,
  }
}
